import React, { useCallback, useEffect } from 'react';
import ForceGuaranteePageView from '../views/ForceGuarantee/Index';
import {
  useForceGuaranteeProducts,
  useForceGuaranteeTransactions,
  useForceGuaranteeEvaluation,
  useForceGuaranteeResult,
  useForceGuaranteeDerived,
  useForceGuaranteeActions,
} from '../hooks/useForceGuarantee';

// Owns the flow: loads the product list on mount, wires user intent to the
// `forceGuarantee` duck and feeds the view flat props. Access control is
// enforced at the route level via authorize().
const ForceGuarantee = () => {
  const { products, productsLoading, productsErrorMessage } = useForceGuaranteeProducts();
  const {
    transactions,
    transactionsLoading,
    transactionsErrorMessage,
    selectedId,
    search,
  } = useForceGuaranteeTransactions();
  const {
    evaluatedId,
    eligibilityResults,
    evaluationLoading,
    evaluationErrorMessage,
  } = useForceGuaranteeEvaluation();
  const {
    forceResult, forceLoading, forceErrorMessage, modalOpen,
  } = useForceGuaranteeResult();
  const {
    busy,
    canForce,
    hasSearched,
    failedRulesCount,
    totalRulesCount,
    evaluatedTransaction,
    selectedProduct,
  } = useForceGuaranteeDerived();
  const {
    loadProducts,
    searchTransactions,
    selectTransaction,
    runEligibility,
    openModal,
    closeModal,
    confirmForce,
    backToTransactions,
    resetFlow,
  } = useForceGuaranteeActions();

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // The slice lives in the global store, so a revisit would otherwise show the
  // previous VIN's results.
  useEffect(() => resetFlow, [resetFlow]);

  const handleRunEligibility = useCallback(
    () => runEligibility(selectedId),
    [runEligibility, selectedId]
  );

  // The force targets the evaluated order, not the selected row — `selectCanForce`
  // keeps the two in sync and gates the button.
  const handleConfirm = useCallback(
    justification => confirmForce(evaluatedId, justification),
    [confirmForce, evaluatedId]
  );

  return (
    <ForceGuaranteePageView
      products={products}
      productsLoading={productsLoading}
      productsErrorMessage={productsErrorMessage}
      transactions={transactions}
      transactionsLoading={transactionsLoading}
      transactionsErrorMessage={transactionsErrorMessage}
      selectedId={selectedId}
      productCode={search.productCode}
      vin={search.vin}
      hasSearched={hasSearched}
      evaluatedId={evaluatedId}
      eligibilityResults={eligibilityResults}
      evaluationLoading={evaluationLoading}
      evaluationErrorMessage={evaluationErrorMessage}
      evaluatedVin={evaluatedTransaction ? evaluatedTransaction.vin : search.vin}
      productName={selectedProduct ? selectedProduct.displayName : ''}
      failedRulesCount={failedRulesCount}
      totalRulesCount={totalRulesCount}
      forceResult={forceResult}
      forceLoading={forceLoading}
      forceErrorMessage={forceErrorMessage}
      modalOpen={modalOpen}
      busy={busy}
      canForce={canForce}
      onSearch={searchTransactions}
      onSelect={selectTransaction}
      onRunEligibility={handleRunEligibility}
      onOpenModal={openModal}
      onCloseModal={closeModal}
      onConfirm={handleConfirm}
      onBack={backToTransactions}
      onReset={resetFlow}
    />
  );
};

export default ForceGuarantee;
